// Shared view-model + label helpers for Shipping Manifest "View Details" modal.
// This is extracted from `shipping_manifest_list.js` so other pages can reuse the exact same mapping/UI.

export function transformManifestData(manifest) {
  if (!manifest || typeof manifest !== 'object') return null;

  const statusMap = {
    Submitted: 'Created',
    submitted: 'Created',
    Accepted: 'Accepted',
    accepted: 'Accepted',
    Draft: 'Draft',
    draft: 'Draft',
  };
  const displayStatus = statusMap[manifest.status] || manifest.status || 'Created';

  const mode = manifest.mode_of_transport || '';
  const type = String(mode).toLowerCase().includes('air') ? 'AIR' : 'SEA';

  return {
    id: manifest.id,
    manifestNumber: manifest.manifest_number || `MF-${manifest.id}`,
    type,
    manifestDate: manifest.manifest_date,
    shippingMethod: type,
    billOfLading: manifest.bill_of_lading || null,
    airWaybill: manifest.airway_bill || null,
    exporter: manifest.from_company_name || 'N/A',
    exporterLocation: `${manifest.from_city || ''}, ${manifest.from_state || ''}, ${manifest.from_country || ''}`.trim(),
    importer: manifest.to_company_name || 'N/A',
    importerLocation: `${manifest.to_city || ''}, ${manifest.to_state || ''}, ${manifest.to_country || ''}`.trim(),
    mode: manifest.mode_of_transport || 'N/A',
    incoterms: manifest.incoterms || 'N/A',
    poNumber: manifest.po_number || 'N/A',
    invoiceNumber: manifest.commercial_invoice_number || manifest.invoice_bill_number || 'N/A',
    lrNumber: manifest.lr_number || null,
    deliveryPort: manifest.preferred_delivery_port || 'N/A',
    deliveryPortCode: manifest.delivery_port_reference || null,
    ewayBillNumber: manifest.eway_bill_number || manifest.ewayBillNumber || null,
    carrier: manifest.carrier_vessel || null,
    vesselOrFlight: manifest.vessel_flight_number || null,
    route: manifest.route || null,
    departureDate: manifest.departure_date || null,
    expectedArrival: manifest.expected_arrival_date || manifest.estimated_delivery_date || null,
    actualArrival: manifest.actual_arrival_date || null,
    finalDestination: manifest.final_destination || null,
    packages: manifest.total_packages || 0,
    weight: manifest.total_weight_kg ? `${manifest.total_weight_kg} kg` : '0 kg',
    status: displayStatus,
    invoiceCount: 1,
    containerCount: 0,
    documentsStatus: 'Docs Pending',
    documents: manifest.documents
      ? {
          invoice: manifest.documents.some((doc) => doc.document_type === 'invoice'),
          packingList: manifest.documents.some((doc) => doc.document_type === 'packing_list'),
          ewayBill: manifest.documents.some((doc) => doc.document_type === 'eway_bill'),
          qualityCert: manifest.documents.some((doc) => doc.document_type === 'quality_certificate'),
          certOfOrigin: manifest.documents.some((doc) => doc.document_type === 'certificate_of_origin'),
          otherDocs: manifest.documents.some((doc) => doc.document_type === 'other_document'),
        }
      : {},
    documentFiles: manifest.documents
      ? manifest.documents.reduce((acc, doc) => {
          const typeMap = {
            invoice: 'invoice',
            packing_list: 'packingList',
            eway_bill: 'ewayBill',
            quality_certificate: 'qualityCert',
            certificate_of_origin: 'certOfOrigin',
            other_document: 'otherDocs',
          };
          const key = typeMap[doc.document_type];
          if (key && doc.document_path) {
            acc[key] = { name: doc.document_name || 'Document', type: 'PDF' };
          }
          return acc;
        }, {})
      : {},
    lastUpdated: manifest.updated_datetime || manifest.created_datetime || null,
    vehicleDetails: {
      vehicleType: manifest.vehicle_type || null,
      vehicleNumber: manifest.vehicle_number || null,
      vehicleCapacity: manifest.vehicle_capacity || null,
      gpsTrackingId: manifest.gps_tracking_id || null,
      gpsProvider: manifest.gps_provider || null,
      insurancePolicyNo: manifest.insurance_policy_no || null,
      insuranceProvider: manifest.insurance_provider || null,
      driverName: manifest.driver_name || null,
      driverLicenseNo: manifest.driver_license_no || null,
      driverContactNumber: manifest.driver_contact_number || null,
    },
    goodsHandling: manifest.goods_handling_requirements
      ? typeof manifest.goods_handling_requirements === 'string'
        ? safeJsonParse(manifest.goods_handling_requirements, {})
        : manifest.goods_handling_requirements
      : {},
    packageItems: manifest.items
      ? manifest.items.map((item) => ({
          hsnProductCode: item.hsn_product_code || 'N/A',
          productDescription: item.product_description || 'N/A',
          qtyShipped: item.qty_shipped || '0',
          qtyTotal: item.qty_total || '0',
          shippingQty: item.shipping_qty || '0',
          uom: item.uom || 'N/A',
          packageType: item.package_type || 'N/A',
          noPackages: item.no_packages || '0',
          weightPerPkg: item.weight_per_pkg_kg || '0',
          totalWeight: item.total_weight_kg ? `${item.total_weight_kg} kg` : '0 kg',
        }))
      : [],
    additionalNotes: manifest.additional_notes || null,
    totals: {
      packages: manifest.total_packages || 0,
      grossWeight: manifest.total_weight_kg ? `${manifest.total_weight_kg} kg` : '0 kg',
      netWeight: manifest.total_weight_kg ? `${manifest.total_weight_kg} kg` : '0 kg',
    },
    // Acceptance details: optional, only present after acceptance
    acceptanceDetails:
      // Only show acceptance section when explicit acceptance fields exist.
      // `actual_arrival_date` can be filled pre-acceptance, so it must NOT trigger the section by itself.
      manifest.accepted_date || manifest.accepted_by || manifest.acceptance_notes || manifest.inspection_checklist
        ? (() => {
            // inspection_checklist column (preferred)
            let inspectionChecklist = {};
            if (manifest.inspection_checklist) {
              if (typeof manifest.inspection_checklist === 'string') {
                inspectionChecklist = safeJsonParse(manifest.inspection_checklist, {});
              } else if (typeof manifest.inspection_checklist === 'object') {
                inspectionChecklist = manifest.inspection_checklist || {};
              }
            }

            // acceptance_notes: may be plain text or JSON (legacy)
            let additionalNotes = manifest.acceptance_notes || null;
            if (!manifest.inspection_checklist && manifest.acceptance_notes) {
              if (typeof manifest.acceptance_notes === 'string') {
                const parsed = safeJsonParse(manifest.acceptance_notes, null);
                if (parsed && typeof parsed === 'object') {
                  if (parsed.inspectionChecklist && typeof parsed.inspectionChecklist === 'object') {
                    inspectionChecklist = parsed.inspectionChecklist;
                  }
                  if (parsed.notes !== undefined) {
                    additionalNotes = parsed.notes || null;
                  }
                }
              }
            }

            // Find POD document from documents array
            let podFile = null;
            if (Array.isArray(manifest.documents)) {
              const podDoc = manifest.documents.find(
                (doc) =>
                  doc?.document_type === 'pod' ||
                  doc?.document_type === 'proof_of_delivery' ||
                  String(doc?.document_type || '').toLowerCase() === 'pod'
              );
              if (podDoc) {
                podFile = {
                  name: podDoc.document_name || 'Proof of Delivery',
                  path: podDoc.document_path,
                  uploadedAt: podDoc.created_datetime || podDoc.updated_datetime,
                };
              }
            }

            return {
              dateReceived: manifest.accepted_date || manifest.actual_arrival_date || null,
              acceptedDate: manifest.accepted_date || null,
              receivedBy: manifest.accepted_by || null,
              packagesReceived: manifest.total_packages != null ? String(manifest.total_packages) : null,
              condition: 'Good Condition',
              additionalNotes,
              inspectionChecklist,
              podFile,
            };
          })()
        : null,
    originalData: manifest,
  };
}

function safeJsonParse(str, fallback) {
  try {
    const v = JSON.parse(str);
    return v && typeof v === 'object' ? v : fallback;
  } catch {
    return fallback;
  }
}

export function getModeOfTransportLabel(modeValue, modeOfTransportOptions) {
  if (!modeValue) return 'N/A';
  if (!Array.isArray(modeOfTransportOptions) || modeOfTransportOptions.length === 0) return modeValue;

  const modeValueNum = Number.isFinite(Number(modeValue)) ? Number(modeValue) : null;
  const found = modeOfTransportOptions.find((opt) => {
    if (String(opt.value) === String(modeValue)) return true;
    if (modeValueNum !== null && Number.isFinite(Number(opt.value))) return Number(opt.value) === modeValueNum;
    return false;
  });
  return found ? found.label : modeValue;
}

export function getIncotermsLabel(incotermValue, shippingTermsOptions) {
  if (!incotermValue) return 'N/A';
  if (!Array.isArray(shippingTermsOptions) || shippingTermsOptions.length === 0) return incotermValue;

  const incNum = Number.isFinite(Number(incotermValue)) ? Number(incotermValue) : null;
  const found = shippingTermsOptions.find((opt) => {
    if (String(opt.value) === String(incotermValue)) return true;
    if (incNum !== null && Number.isFinite(Number(opt.value))) return Number(opt.value) === incNum;
    return false;
  });
  return found ? found.label : incotermValue;
}

